/**
 * Porta Retrato configurator — the PDP's buy box and the cart drawer's edit form.
 *
 * Extracted from the `{% javascript %}` block of the section. The move is about
 * REACHABILITY, not availability: Shopify concatenates every section's javascript
 * tag into one `compiled_assets/scripts.js` that every page already loads, so the
 * old code WAS present on a collection page — it was simply an IIFE whose only
 * entry point was `document.querySelectorAll('.pr')`. With nothing exported, the
 * drawer had no way to call it, which is why a picture frame was the one
 * personalizable surface with no editor.
 *
 * The PDP and the drawer's form share `readState` / `writeState` and the same
 * `data-*` hooks, so a frame edited from the bag and one edited from `?edit=`
 * cannot drift apart. Panel protocol: see assets/cart-edit-host.js.
 *
 * None of the form's own attributes may be `data-action` or `data-edit-*`: the
 * drawer delegates clicks on `[data-action]` across the whole dialog and the host
 * looks up `[data-edit-*]` the same way, so either name would hand a form control
 * to somebody else's handler. Pinned by test.
 */
import { resolveInstallments } from './pdp-installments.js';
import { sanitizeText } from './pdp-engraving-allowlist.js';
import { applyOrigin, originsOfGroup } from './addon-origin.js';

// ── 1. Pure ────────────────────────────────────────────────────────────────

/** The complete configurable state of one picture frame. Ids, never labels. */
export const emptyState = () => ({
  colorLabel: '',
  engraveOn: false,
  engraveWord: '',
  foilId: '',
  orientId: '',
  photoOn: false,
});

export const WORD_MAX = 22;

export const cleanWord = (raw) => sanitizeText(raw).trim().slice(0, WORD_MAX);

export function bindWordInput(input, onChange) {
  if (!input) return;
  input.addEventListener('input', () => {
    const clean = sanitizeText(input.value).slice(0, WORD_MAX);
    if (clean !== input.value) input.value = clean;
    onChange();
  });
}

const findById = (options, id) => (options || []).find((option) => option.id === id) || null;

/**
 * The id whose option carries `label`, falling back to the first option.
 *
 * The cart stores the merchant-facing LABEL ("Prata", "Deitado") because that is
 * what the warehouse reads off the order; the DOM and this module work in stable
 * ids. Hydration is the one place the two meet.
 */
export const idForLabel = (options, label) => {
  const list = options || [];
  const matched = list.find((option) => option.label === label);
  return (matched || list[0] || { id: '' }).id;
};

/** 'h' when the engraving is on AND lying down — the only case using the wide render. */
export const orientKeyFor = (config, state) => {
  if (!state.engraveOn) return 'v';
  const orient = findById(config.orientations, state.orientId);
  return orient && orient.id === 'deitado' ? 'h' : 'v';
};

const withWidth = (src, width) => src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;

export const PREVIEW_TEXT_POS = {
  h: { x: 50, y: 72.2, size: 2.5 },
  v: { x: 50, y: 80.2, size: 2.5 },
};

/**
 * The render the cart shows as the line thumbnail, for this colour and
 * orientation. Empty when the colour has no render yet (Preto) — the cart then
 * falls back to the product photo on its own.
 */
export function coverImageFor(config, state) {
  if (!config.framePreviews || !state.colorLabel) return '';
  const entry = config.framePreviews[state.colorLabel];
  const src = entry && entry[orientKeyFor(config, state)];
  return src ? withWidth(src, 360) : '';
}

export const newGroupId = () => ((typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : 'g' + Date.now() + Math.round(Math.random() * 1e6));

/**
 * The `/cart/add.js` payload for one frame: the frame itself, plus a line of its
 * own for each add-on, all three tied together by `_group`.
 *
 * The add-ons are separate LINES rather than properties because each one is a
 * real Shopify variant that has to be picked, priced and fulfilled on its own —
 * which is also why an edit has to replace the whole group at once.
 */
export function buildCartItems(config, state, group, origins = {}) {
  const mainProps = { _group: group };
  if (state.colorLabel) mainProps['Cor da moldura'] = state.colorLabel;
  const cover = coverImageFor(config, state);
  if (cover) mainProps._cover_image = cover;
  // The engraving's three decisions on the MAIN line too, legible, in the
  // album's vocabulary: the drawer renders 'Cor da gravação' with an icon and
  // 'Palavra'/'Orientação' through its property loop — the add-on chip shows
  // only the word, and Prata/Dourado and Em pé/Deitado were invisible to the
  // customer (QA 24/08). The add-on line's keys below ('Gravação',
  // 'Cor do texto', 'Orientação') do NOT change: the OMS consumes them
  // (ecomm-order-sync in the monorepo).
  if (state.engraveOn) {
    const mainFoil = findById(config.foils, state.foilId);
    if (mainFoil && mainFoil.label) mainProps['Cor da gravação'] = mainFoil.label;
    const mainWord = (state.engraveWord || '').trim();
    if (mainWord) mainProps['Palavra'] = mainWord;
    const mainOrient = findById(config.orientations, state.orientId);
    if (mainOrient && mainOrient.label) mainProps['Orientação'] = mainOrient.label;
  }
  const items = [{ id: config.variantId, quantity: 1, properties: mainProps }];

  if (state.engraveOn && config.engrave && config.engrave.variantId) {
    const engraveProps = { _group: group, _addon: 'gravacao' };
    const word = cleanWord(state.engraveWord);
    if (word) engraveProps['Gravação'] = word;
    const foil = findById(config.foils, state.foilId);
    if (foil && foil.label) engraveProps['Cor do texto'] = foil.label;
    const orient = findById(config.orientations, state.orientId);
    if (orient && orient.label) engraveProps['Orientação'] = orient.label;
    items.push({ id: config.engrave.variantId, quantity: 1, properties: applyOrigin(engraveProps, origins) });
  }

  if (state.photoOn && config.photo && config.photo.variantId) {
    items.push({ id: config.photo.variantId, quantity: 1, properties: applyOrigin({ _group: group, _addon: 'foto' }, origins) });
  }
  return items;
}

/**
 * Read one edited frame back out of a `/cart.js` payload.
 *
 * Returns the group's line keys — everything a save has to zero — alongside the
 * state that rebuilt it. Null when the key is gone, which is a real case: the
 * bag can change between opening the editor and saving from it.
 *
 * The frame colour is restored even though neither editor lets you change it.
 * A save rebuilds the whole group from this state, so a colour that were not
 * read here would be dropped by the very act of toggling the extra photo.
 */
export function readGroup(cart, editKey, config) {
  const items = (cart && cart.items) || [];
  const line = items.find((item) => item.key === editKey);
  if (!line) return null;
  const group = (line.properties || {})._group;
  const groupItems = group
    ? items.filter((item) => (item.properties || {})._group === group)
    : [line];
  const addonOf = (item) => (item.properties || {})._addon;
  const main = groupItems.find((item) => !addonOf(item)) || line;
  const engraveLine = groupItems.find((item) => addonOf(item) === 'gravacao');
  const engraveProps = (engraveLine && engraveLine.properties) || {};

  return {
    gift: !!(main.properties || {})._fn_brinde,
    // The group id, not just the keys: an add re-keys the bag, so the keys read
    // here stop naming these lines the moment a replacement lands. `_group` is
    // ours and survives it — see saveGroup's removal step.
    group: group || null,
    keys: groupItems.map((item) => item.key),
    origins: originsOfGroup(groupItems),
    state: {
      colorLabel: (main.properties || {})['Cor da moldura'] || '',
      engraveOn: !!engraveLine,
      engraveWord: engraveProps['Gravação'] || '',
      foilId: idForLabel(config.foils, engraveProps['Cor do texto']),
      orientId: idForLabel(config.orientations, engraveProps['Orientação']),
      photoOn: !!groupItems.some((item) => addonOf(item) === 'foto'),
    },
  };
}

// ── The cart, over injectable transport ────────────────────────────────────
// Both calls answer `{ ok, data }` so a 422 from Shopify — which carries the
// reason in its body — is a value to read rather than an exception to catch.
export const browserHttp = {
  getJson: (url) => fetch(url, { headers: { Accept: 'application/json' } })
    .then((response) => response.json().then((data) => ({ ok: response.ok, data }))),
  postJson: (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => response.json().then((data) => ({ ok: response.ok, data }))),
};

/**
 * What the save it belongs to had already done when it failed: the group it was
 * creating, the keys it still has to drop, the payload it was sending, and
 * whether the replacement is in. A retry passes it back so the save resumes
 * instead of starting over — see `saveGroup`.
 */
export const payloadSignature = (config, state) => JSON.stringify(buildCartItems(config, state, ''));

/**
 * A save that failed, carrying what of it is already done.
 */
export class EditSaveError extends Error {
  constructor(message, pending) {
    super(message);
    this.name = 'EditSaveError';
    this.pending = pending || null;
  }
}

/**
 * Replace the edited frame's whole group: add the replacement, then remove the
 * old lines.
 *
 * ── Why add first ───────────────────────────────────────────────────────────
 * Because the failure that matters is losing the buyer's work, and only this
 * order cannot cause it. If the add fails — the variant sold out, the network
 * dropped — nothing has been removed and the bag still holds what they had.
 *
 * This reverses what the album did first, on a premise that turned out to be
 * false: that Shopify cannot target one of two line items sharing a variant, so
 * the replacement had to be added into an empty space. A line key is
 * `variantId:hash(properties)`, and every save mints a fresh `_group` — so the
 * old line and its replacement never share a key and never merge. Verified
 * against the store: two frames of one variant differing only in `_group` came
 * back as two lines with distinct keys, and removing one by key left the other
 * standing.
 *
 * The removal is ONE `/cart/update.js` call, the same one the drawer's own
 * "remove this group" button makes (`removeGroup` in sections/cart-drawer.liquid).
 * A per-line loop can fail halfway and orphan the add-ons around a frame that is
 * already gone. Atomic, there is no half.
 *
 * The one failure left — added, then the removal fell over — leaves the bag
 * holding both versions. That is visible and the buyer can delete one, which is
 * why it is the failure worth having; it throws with `pending` so a retry
 * finishes the removal rather than adding a third copy.
 *
 * `fallbackKeys` are the keys read when the screen opened. They stand in only
 * when the bag cannot be re-read at all — a stale key either names the same
 * logical line or matches nothing, so it cannot zero somebody else's line.
 *
 * Returns the rendered `cart-drawer` section, so the caller can re-render the bag
 * without a second round trip.
 */
export async function saveGroup({
  config, state, editKey, fallbackKeys, http = browserHttp, sectionsUrl = '', pending = null, itemsFor = null, origins = {},
}) {
  const strings = config.strings || {};
  // The payload builder is injectable because this engine is not frame-specific:
  // the group bookkeeping (add first, track by _group, resolve keys after the
  // add) is what the "Apple" beta PDPs reuse for their own lines — each hands in
  // its own builder and keeps its own property contract. The internal readGroup
  // call below stays correct for any product: the group discovery only needs
  // `_group`/`key`, and the frame-specific fields it also returns go unread.
  const itemsBuilder = itemsFor || ((group, groupOrigins) => buildCartItems(config, state, group, groupOrigins));
  // The idempotency key identifies the PAYLOAD, not the attempt. The Cart AJAX
  // API offers none, so the group uuid stands in — but only while the buyer has
  // not changed anything: the form stays editable behind a failed save, and
  // reusing the id after an edit would skip the add and leave the bag holding a
  // version they never confirmed.
  const signature = itemsFor ? JSON.stringify(itemsFor('')) : payloadSignature(config, state);
  const sameAsAttempt = !!pending && pending.signature === signature;
  const groupId = sameAsAttempt ? pending.groupId : newGroupId();
  let oldKeys = pending ? pending.oldKeys : (fallbackKeys || []);
  let oldGroups = pending ? (pending.oldGroups || []) : [];
  let replacementIn = !!(sameAsAttempt && pending.replacementIn);
  let addonOrigins = origins || {};
  // Whether THIS call is the one that added. Only an add invalidates the line
  // keys read above, so only it has to resolve them again before removing.
  let addedNow = false;
  let sections = null;
  const attempt = () => ({ groupId, oldKeys, oldGroups, signature, replacementIn });

  if (!replacementIn) {
    const cart = await http.getJson(config.routes.cart).catch(() => null);
    // A retry that cannot see the bag cannot tell whether its own previous add
    // landed. Adding blind is exactly how two replacements happen.
    if (!(cart && cart.ok) && pending) throw new EditSaveError(strings.addError || '', pending);
    if (cart && cart.ok) {
      const items = (cart.data && cart.data.items) || [];
      const keysOfGroup = (id) => items.filter((item) => (item.properties || {})._group === id).map((item) => item.key);
      // A previous attempt that no longer matches what the buyer wants left a
      // group behind nobody asked for. It goes out with the old one.
      const staleKeys = pending && !sameAsAttempt ? keysOfGroup(pending.groupId) : [];
      const staleGroups = pending && !sameAsAttempt && pending.groupId ? [pending.groupId] : [];

      if (sameAsAttempt && keysOfGroup(groupId).length) {
        replacementIn = true;
      } else {
        const current = readGroup(cart.data, editKey, config);
        if (current && current.gift) throw new EditSaveError(strings.addError || '', null);
        if (current) {
          addonOrigins = current.origins || addonOrigins;
          // Both, because they answer different questions: the keys are what a
          // save that adds nothing zeroes right away, and the group is what
          // re-finds these lines once an add has re-keyed the bag.
          oldKeys = current.keys.concat(staleKeys.filter((key) => !current.keys.includes(key)));
          oldGroups = (current.group ? [current.group] : []).concat(staleGroups.filter((id) => id !== current.group));
        } else if (staleKeys.length) {
          // The line this screen opened on is gone, but the abandoned attempt's
          // group is in the bag — that group is what this save now replaces,
          // rather than being left behind forever.
          oldKeys = staleKeys;
          oldGroups = staleGroups;
        } else {
          // Nothing of this frame is left: it was removed somewhere else while
          // the screen was open, and adding would resurrect what they deleted.
          throw new EditSaveError(strings.lineGone || strings.addError || '', null);
        }
      }
    }
  }

  if (!replacementIn) {
    let added;
    try {
      added = await http.postJson(config.routes.cartAdd, {
        items: itemsBuilder(groupId, addonOrigins),
        sections: 'cart-drawer',
        sections_url: sectionsUrl,
      });
    } catch {
      // The POST may well have reached Shopify before this threw. Keeping the
      // group id is what lets the retry go and find out, instead of minting a
      // new one and adding a second copy.
      throw new EditSaveError(strings.addError || '', attempt());
    }
    // Nothing was removed, so the bag still holds exactly what the buyer had.
    if (!added.ok) {
      const reason = added.data && (added.data.description || added.data.message);
      throw new EditSaveError(reason || strings.addError || '', attempt());
    }
    sections = (added.data && added.data.sections) || sections;
    replacementIn = true;
    addedNow = true;
  }

  const updates = {};
  if ((addedNow || replacementIn) && oldGroups.length) {
    // Resolve the keys NOW, because the add this call just made is what
    // invalidates the ones read above. Zeroing a stale key removes the
    // REPLACEMENT instead of the original, and the damage is quiet: the buyer
    // keeps the old frame, while the new add-ons stay behind in a group no frame
    // carries — the drawer only draws an add-on inside the line whose `_group`
    // matches, so it vanishes from the bag and is still charged at checkout.
    // `replacementIn` counts too: a retry that adds nothing because a previous
    // attempt already did is holding keys THAT add invalidated. Only a save with
    // no replacement in the bag at all can trust the keys it read.
    const bag = await http.getJson(config.routes.cart).catch(() => null);
    // Both versions are in the bag and nothing here can tell which keys to zero.
    // Better a duplicate the buyer can see and delete than a guess that removes
    // the wrong one. A body without an items array is as useless as no body:
    // it would resolve to zero keys and end the save with the old copy in.
    if (!(bag && bag.ok && bag.data && Array.isArray(bag.data.items))) {
      throw new EditSaveError(strings.oldCopyLeft || strings.addError || '', { ...attempt(), sections });
    }
    ((bag.data && bag.data.items) || []).forEach((item) => {
      const itemGroup = (item.properties || {})._group;
      if (itemGroup && itemGroup !== groupId && oldGroups.indexOf(itemGroup) > -1) updates[item.key] = 0;
    });
  } else {
    oldKeys.forEach((oldKey) => { updates[oldKey] = 0; });
  }
  // The old group is already gone — removed elsewhere, or by a retry that got
  // further than it managed to record. An empty update is a no-op.
  if (!Object.keys(updates).length) return sections;

  const removed = await http.postJson(config.routes.cartUpdate, {
    updates, sections: 'cart-drawer', sections_url: sectionsUrl,
  });
  if (!removed.ok) {
    // Both versions are in the bag. `sections` rides along so the caller can
    // re-render it: the message tells the buyer to delete the old copy, and that
    // advice is only actionable once they can actually see it.
    throw new EditSaveError(strings.oldCopyLeft || strings.addError || '', { ...attempt(), sections });
  }
  return (removed.data && removed.data.sections) || sections;
}

// ── 2. State ↔ DOM ─────────────────────────────────────────────────────────
// The same two functions serve both surfaces, because the panel's form carries
// the same hooks the Liquid configurator does.

const all = (root, selector) => Array.prototype.slice.call(root.querySelectorAll(selector));

/**
 * Read the state out of whatever `root` renders.
 *
 * `base` supplies whatever this root has no control for. The drawer's form is
 * exactly that case: it never shows the frame colours, so the colour has to
 * survive in the state rather than be re-read from a swatch that is not there.
 */
export function readState(root, base) {
  const state = { ...emptyState(), ...(base || {}) };
  const selectedColor = root.querySelector('[data-color-swatch].is-selected');
  if (selectedColor) state.colorLabel = selectedColor.getAttribute('data-label') || '';

  const engraveToggle = root.querySelector('[data-engrave-toggle]');
  if (engraveToggle) state.engraveOn = engraveToggle.getAttribute('aria-checked') === 'true';
  const wordInput = root.querySelector('[data-engrave-word]');
  if (wordInput) state.engraveWord = cleanWord(wordInput.value);
  const foil = root.querySelector('[data-engrave-color].is-selected');
  if (foil) state.foilId = foil.getAttribute('data-foil') || '';
  const orient = root.querySelector('[data-engrave-orient] .is-selected');
  if (orient) state.orientId = orient.getAttribute('data-orient') || '';

  const photoToggle = root.querySelector('[data-photo-toggle]');
  if (photoToggle) state.photoOn = photoToggle.getAttribute('aria-checked') === 'true';
  return state;
}

const setSelected = (buttons, isOn) => {
  buttons.forEach((button) => {
    const on = isOn(button);
    button.classList.toggle('is-selected', on);
    button.setAttribute('aria-checked', on ? 'true' : 'false');
  });
};

const setSwitch = (toggle, fields, on) => {
  if (!toggle) return;
  toggle.setAttribute('aria-checked', on ? 'true' : 'false');
  toggle.classList.toggle('is-on', on);
  if (fields) fields.hidden = !on;
};

/** Push a state onto whatever `root` renders. Controls it lacks are skipped. */
export function writeState(root, state) {
  const swatches = all(root, '[data-color-swatch]');
  if (swatches.length) {
    setSelected(swatches, (swatch) => swatch.getAttribute('data-label') === state.colorLabel);
    const readout = root.querySelector('[data-color-readout]');
    if (readout) readout.textContent = state.colorLabel || '';
  }

  const wordInput = root.querySelector('[data-engrave-word]');
  if (wordInput) wordInput.value = state.engraveWord || '';
  setSelected(all(root, '[data-engrave-color]'), (button) => button.getAttribute('data-foil') === state.foilId);
  setSelected(all(root, '[data-engrave-orient] [data-orient]'), (button) => button.getAttribute('data-orient') === state.orientId);

  setSwitch(root.querySelector('[data-engrave-toggle]'), root.querySelector('[data-engrave-fields]'), state.engraveOn);
  setSwitch(root.querySelector('[data-photo-toggle]'), root.querySelector('[data-photo-fields]'), state.photoOn);
}

// ── 3. The PDP ─────────────────────────────────────────────────────────────

const pageDocument = typeof document === 'undefined' ? null : document;
const pageWindow = typeof window === 'undefined' ? null : window;

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const money = (cents) => BRL.format((cents || 0) / 100);

/**
 * Bind one rendered `.pr` section to the shared layers above.
 *
 * Every handler ends at `sync()`: the DOM is what the buyer manipulates, so it
 * stays the source of truth and `state` is re-read from it rather than kept in
 * step by hand. That is also what lets `?edit=` hydration be a single
 * `writeState` + `sync` instead of a second copy of every update rule.
 */
export function initPortaRetrato(sectionRoot) {
  if (sectionRoot.dataset.prReady) return;
  sectionRoot.dataset.prReady = '1';

  const island = sectionRoot.querySelector('.pr-config-data');
  if (!island) return;
  const config = JSON.parse(island.textContent);
  const strings = config.strings || {};

  const query = (selector) => sectionRoot.querySelector(selector);
  const queryAll = (selector) => all(sectionRoot, selector);

  let state = emptyState();

  const els = {
    priceNow: query('[data-price-now]'),
    priceWas: query('[data-price-was]'),
    priceOff: query('[data-price-off]'),
    installment: query('[data-price-installment]'),
    pix: query('[data-price-pix]'),
    recapDesc: query('[data-recap-desc]'),
    recapTotal: query('[data-recap-total]'),
    colorReadout: query('[data-color-readout]'),
    colorSwatches: queryAll('[data-color-swatch]'),
  };

  // ── Headline price (derived from the base variant) ──
  function renderHeadline() {
    const base = config.basePrice || 0;
    const compare = config.compareAt || 0;
    const hasDiscount = compare > base && compare > 0;
    if (els.priceNow) els.priceNow.textContent = money(base);
    if (els.priceWas) {
      els.priceWas.textContent = money(compare);
      els.priceWas.hidden = !hasDiscount;
    }
    if (els.priceOff) {
      const percent = hasDiscount ? Math.round((1 - base / compare) * 100) : 0;
      els.priceOff.textContent = '-' + percent + '%';
      els.priceOff.hidden = !hasDiscount;
    }
    // The bounds — and the missing-island fallback — belong to the shared
    // resolver, which is the only place the formula is written down.
    const { count, perInstallmentCents } = resolveInstallments(base, config.installmentsRule);
    if (els.installment) {
      els.installment.textContent = (strings.installments || '{count}x {price}')
        .replace('{count}', count)
        .replace('{price}', money(perInstallmentCents));
    }
    if (els.pix) {
      const pixCents = Math.round(base * (1 - (Number(config.pixPercent) || 0) / 100));
      els.pix.textContent = (strings.pix || '{price}').replace('{price}', money(pixCents));
    }
  }

  // ── Recap total = base + toggled add-ons (real add-on variant prices) ──
  function recompute() {
    const engravePrice = (config.engrave && config.engrave.price) || config.engraveSurcharge || 0;
    const photoPrice = (config.photo && config.photo.price) || 0;
    const total = (config.basePrice || 0)
      + (state.engraveOn ? engravePrice : 0)
      + (state.photoOn ? photoPrice : 0);
    if (els.recapDesc) {
      const parts = [strings.recapPrefix || ''];
      if (state.engraveOn) parts.push(strings.recapEngrave);
      if (state.photoOn) parts.push(strings.recapPhoto);
      els.recapDesc.textContent = parts.join(' + ');
    }
    if (els.recapTotal) els.recapTotal.textContent = money(total);
  }

  // ── Live personalization preview ──
  // Picking a colour swaps in that colour's frame render ("Em pé" → the vertical
  // shot, "Deitado" → the horizontal one) and, with gravação on, overlays the
  // word in the frame's bottom mat with the chosen foil. Anchors are % / cqw of
  // the shared 1915×1634 studio canvas.
  const previewStage = query('[data-pr-preview]');
  const previewBox = query('[data-pr-preview-box]');
  const previewImg = query('[data-pr-preview-img]');
  const previewText = query('[data-pr-preview-text]');
  let previewLoadToken = 0;

  const previewUrl = (src) => withWidth(src, 1400);

  // Pre-warm every colour render once the page has settled, so the first click
  // (and every colour change after it) swaps straight from cache. Vertical
  // first — it is the default orientation — then horizontal; the browser's
  // per-host queue keeps that priority order.
  let previewsWarmed = false;
  function warmPreviews() {
    if (previewsWarmed || !config.framePreviews) return;
    previewsWarmed = true;
    ['v', 'h'].forEach((orientKey) => {
      Object.keys(config.framePreviews).forEach((label) => {
        const entry = config.framePreviews[label];
        if (entry && entry[orientKey]) { new Image().src = previewUrl(entry[orientKey]); }
      });
    });
  }
  function scheduleWarmPreviews() {
    const kickoff = () => {
      if (window.requestIdleCallback) requestIdleCallback(warmPreviews, { timeout: 4000 });
      else setTimeout(warmPreviews, 1500);
    };
    if (document.readyState === 'complete') kickoff();
    else window.addEventListener('load', kickoff, { once: true });
  }
  scheduleWarmPreviews();

  function updatePreview() {
    if (!previewStage || !config.framePreviews) return;
    const entry = state.colorLabel ? config.framePreviews[state.colorLabel] : null;
    const orientKey = orientKeyFor(config, state);
    const src = entry && entry[orientKey];
    if (!src) {
      // Colour without a render yet (e.g. Preto): fall back to the photo gallery.
      previewStage.hidden = true;
      return;
    }
    const sized = previewUrl(src);
    if (previewImg.getAttribute('src') !== sized) {
      // Decode-then-swap keeps the previous render on screen (no blank flash);
      // the token discards stale loads when colours are clicked in quick succession.
      const token = ++previewLoadToken;
      const loader = new Image();
      loader.onload = () => {
        if (token === previewLoadToken) previewImg.src = sized;
      };
      loader.onerror = () => {
        // Failed load: keep the last good render on screen; with nothing
        // rendered yet, fall back to the photo gallery instead of a broken img.
        if (token === previewLoadToken && !previewImg.getAttribute('src')) previewStage.hidden = true;
      };
      loader.src = sized;
    }
    const other = entry[orientKey === 'v' ? 'h' : 'v'];
    if (other) { new Image().src = previewUrl(other); } // warm the flip side
    previewStage.hidden = false;

    previewBox.classList.toggle('pr-preview--gold', state.foilId === 'gold');
    previewBox.classList.toggle('pr-preview--silver', state.foilId !== 'gold');
    const position = PREVIEW_TEXT_POS[orientKey];
    previewText.style.setProperty('--text-x', position.x + '%');
    previewText.style.setProperty('--text-y', position.y + '%');
    previewText.style.setProperty('--text-size', position.size + 'cqw');
    // Gravação ligada com campo vazio mostra a palavra do placeholder apagada
    // (mesmo esquema das PDPs novas); o "Ex:" do campo nunca vai gravado.
    const exampleWord = (config.labels && config.labels.wordPlaceholder) || '';
    previewText.textContent = state.engraveWord || exampleWord;
    previewText.classList.toggle('pr-preview__line--empty', !state.engraveWord);
    previewText.hidden = !state.engraveOn || !(state.engraveWord || exampleWord);
  }

  // ── Production deadline follows the engraving toggle ──
  // The estimator renders with the porta's base days; engraving is produced by
  // another team and stretches production (config.prazoDays.engraved, from the
  // gravação product's prazos.config). The estimator recomputes from its JSON
  // config island on every quote, so rewriting the island keeps future quotes
  // correct; the visible produção row and an already-computed total are patched
  // in place.
  const prazoRoot = query('[data-pr-prazo] [data-prazo]');
  if (prazoRoot) {
    prazoRoot.addEventListener('prazo:request-lines', (event) => {
      event.detail.lines = buildCartItems(config, state, '');
    });
  }
  function updatePrazoDays() {
    if (!prazoRoot || !config.prazoDays) return;
    const days = state.engraveOn ? config.prazoDays.engraved : config.prazoDays.base;
    const prazoIsland = prazoRoot.querySelector('[data-prazo-config]');
    if (!prazoIsland) return;
    let parsed;
    try { parsed = JSON.parse(prazoIsland.textContent); } catch { return; }
    if (parsed.producao === days) return;
    parsed.producao = days;
    prazoIsland.textContent = JSON.stringify(parsed);

    const unit = (parsed.strings && parsed.strings.businessDaysUnit) || 'dias úteis';
    const producaoEl = prazoRoot.querySelector('[data-prazo-producao-days]');
    if (producaoEl) producaoEl.textContent = days + ' ' + unit;

    const totalEl = prazoRoot.querySelector('[data-prazo-total-days]');
    if (totalEl && totalEl.textContent.trim()) {
      const deliveryEl = prazoRoot.querySelector('[data-prazo-delivery-days]');
      const deliveryDays = parseInt(deliveryEl && deliveryEl.textContent, 10) || 0;
      totalEl.textContent = ((parsed.diagramacao || 0) + days + deliveryDays) + ' ' + unit;
    }
  }

  /** Re-read the DOM, then bring everything derived from it back in step. */
  function sync() {
    state = readState(sectionRoot, state);
    if (els.colorReadout && state.colorLabel) els.colorReadout.textContent = state.colorLabel;
    recompute();
    updatePreview();
    updatePrazoDays();
  }

  // ── Controls ──
  // The colour swatches own their radio state here, so `is-selected` is already
  // on the clicked swatch by the time sync() reads it back.
  els.colorSwatches.forEach((swatch) => {
    swatch.addEventListener('click', () => {
      setSelected(els.colorSwatches, (other) => other === swatch);
      sync();
    });
  });

  const bindRadioGroup = (buttons) => {
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        setSelected(buttons, (other) => other === button);
        sync();
      });
    });
  };
  bindRadioGroup(queryAll('[data-engrave-color]'));
  bindRadioGroup(queryAll('[data-engrave-orient] [data-orient]'));

  bindWordInput(query('[data-engrave-word]'), sync);

  const bindToggle = (toggle, fields) => {
    if (!toggle) return;
    toggle.addEventListener('click', () => {
      setSwitch(toggle, fields, toggle.getAttribute('aria-checked') !== 'true');
      sync();
    });
  };
  bindToggle(query('[data-engrave-toggle]'), query('[data-engrave-fields]'));
  bindToggle(query('[data-photo-toggle]'), query('[data-photo-fields]'));

  // ── Add to cart / save the edit ──
  const atc = query('[data-atc]');
  const atcNotice = query('[data-atc-notice]');
  const atcLabel = query('[data-atc-label]');
  const defaultAtcLabel = atcLabel ? atcLabel.textContent : '';

  const showAtcError = (message) => {
    if (atcNotice) { atcNotice.textContent = message || ''; atcNotice.hidden = false; }
  };

  // `?edit=<line_key>` is where the cart has always linked. Until now nothing
  // read it and the buyer landed on a blank PDP; hydrating it here is what makes
  // that link — the no-JS and no-mounter path — actually edit.
  const editKey = pageWindow ? new URLSearchParams(pageWindow.location.search).get('edit') : null;
  let editKeys = null;
  let editOrigins = {};
  // See mountPanel: once the replacement is in, a retry only finishes the removal.
  let pendingSave = null;

  async function hydrateEdit() {
    if (!editKey) return;
    try {
      const cart = await browserHttp.getJson(config.routes.cart);
      if (!cart.ok) return;
      const group = readGroup(cart.data, editKey, config);
      // A key the cart no longer holds leaves the PDP exactly as it was: an
      // ordinary, addable picture frame. Graceful, no error.
      if (!group || group.gift) return;
      editKeys = group.keys;
      editOrigins = group.origins || {};
      writeState(sectionRoot, group.state);
      sync();
      if (atcLabel && strings.saveChanges) atcLabel.textContent = strings.saveChanges;
    } catch {
      /* graceful fallback: stay on the ordinary buy box */
    }
  }

  if (atc && config.variantId && config.routes && config.routes.cartAdd) {
    atc.addEventListener('click', async () => {
      // Colour is a real production input (one colour per piece) and nothing is
      // pre-selected — block the add until the customer picks one.
      if (!state.colorLabel) {
        showAtcError(strings.chooseColor);
        return;
      }
      atc.disabled = true;
      if (atcLabel) atcLabel.textContent = strings.adding || defaultAtcLabel;
      if (atcNotice) atcNotice.hidden = true;
      const sectionsUrl = pageWindow ? pageWindow.location.pathname : '';

      try {
        if (editKeys) {
          const sections = await saveGroup({
            config, state, editKey, fallbackKeys: editKeys, sectionsUrl, pending: pendingSave, origins: editOrigins,
          });
          document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
        } else {
          const added = await browserHttp.postJson(config.routes.cartAdd, {
            items: buildCartItems(config, state, newGroupId()),
            sections: 'cart-drawer',
            sections_url: sectionsUrl,
          });
          if (!added.ok) {
            showAtcError((added.data && (added.data.description || added.data.message)) || strings.addError);
            return;
          }
          // The drawer hydrates itself from the bundled section render and opens
          // via its `cart:open` hook (which also refreshes the cart badge).
          document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: added.data.sections } }));
        }
      } catch (error) {
        if (error && error.pending) pendingSave = error.pending;
        // See mountPanel: make the copy the message names actually visible.
        const stranded = error && error.pending && error.pending.sections;
        if (stranded) document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: stranded } }));
        showAtcError(error.message || strings.addError);
      } finally {
        atc.disabled = false;
        if (atcLabel) atcLabel.textContent = editKeys ? (strings.saveChanges || defaultAtcLabel) : defaultAtcLabel;
      }
    });
  }

  // ── Details accordion ──
  const detailsBar = query('[data-details-bar]');
  const detailsBody = query('[data-details-body]');
  const detailsIcon = query('[data-details-icon] svg path');
  if (detailsBar) {
    detailsBar.addEventListener('click', () => {
      const open = detailsBar.getAttribute('aria-expanded') === 'true';
      detailsBar.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (detailsBody) detailsBody.hidden = open;
      if (detailsIcon) detailsIcon.setAttribute('d', open ? 'M12 5v14M5 12h14' : 'M5 12h14');
    });
  }

  // ── Gallery thumbnails ──
  const gallery = query('[data-pr-gallery]');
  if (gallery) {
    gallery.addEventListener('click', (event) => {
      const thumb = event.target.closest('.pf-gallery__thumb');
      if (!thumb) return;
      // Browsing the photos dismisses the personalization preview; any
      // personalization interaction brings it back.
      if (previewStage) previewStage.hidden = true;
      const index = thumb.getAttribute('data-thumb');
      gallery.querySelectorAll('.pf-gallery__thumb').forEach((other) => {
        other.classList.toggle('is-active', other === thumb);
      });
      gallery.querySelectorAll('.pf-gallery__slide').forEach((slide) => {
        slide.classList.toggle('is-active', slide.getAttribute('data-slide') === index);
      });
    });
  }

  // No initial colour selection: the swatches start unchecked on purpose — the
  // customer's own click is what kicks off the personalization preview. Edit
  // mode is the exception, and hydrateEdit() supplies the colour it restores.
  renderHeadline();
  sync();
  hydrateEdit();
}

// ── 4. The drawer's edit screen ────────────────────────────────────────────

/** One element, built against `doc`. `text` fills it; every other key is an attribute. */
// `svg` and `path` have to be created in the SVG namespace: createElement would
// build an unknown HTML element of the same name, which renders nothing.
const SVG_NS = 'http://www.w3.org/2000/svg';
const SVG_TAGS = ['svg', 'path'];

const makeEl = (doc) => function el(tag, attributes, ...children) {
  const node = SVG_TAGS.indexOf(tag) > -1 ? doc.createElementNS(SVG_NS, tag) : doc.createElement(tag);
  Object.keys(attributes || {}).forEach((name) => {
    const value = attributes[name];
    if (value === false || value === null || value === undefined) return;
    if (name === 'text') node.textContent = value;
    else node.setAttribute(name, value === true ? '' : String(value));
  });
  node.append(...children.filter(Boolean));
  return node;
};

// The word field needs a label to point at, and two edit screens can exist in
// one document while the first is being torn down.
let panelFieldSeq = 0;

/**
 * The flat form: the frame colour and the two add-on blocks the PDP renders, in
 * the same order and under the same labels, and nothing else.
 *
 * The colour carries the same hooks the Liquid swatches do — `data-color-swatch`
 * with the label on it, and `data-color-readout` — so readState/writeState drive
 * it here exactly as they drive the PDP, and the label reaching the line item
 * (plus the `_cover_image` derived from it) comes out of the same code path.
 *
 * Classes are the section's own `pr-` and `pf-` ones. Shopify compiles every
 * section's stylesheet tag into the one theme-wide stylesheet it serves on every
 * page, so this form is styled inside the drawer on a collection page too,
 * without shipping a second asset.
 */
export function buildPanelForm(config, doc = pageDocument) {
  const el = makeEl(doc);
  const labels = config.labels || {};
  const wordId = 'pr-edit-word-' + (++panelFieldSeq);

  const addonHead = (title, price, subtitle, toggleHook) => el(
    'div', { class: 'pr-addon__head' },
    el(
      'div', { class: 'pr-addon__info' },
      el(
        'div', { class: 'pr-addon__titlerow' },
        el('span', { class: 'pr-addon__title', text: title }),
        price && el('span', { class: 'pr-addon__price', text: price }),
      ),
      subtitle && el('span', { class: 'pr-addon__sub', text: subtitle }),
    ),
    el(
      'button',
      { type: 'button', class: 'pr-toggle', role: 'switch', 'aria-checked': 'false', 'aria-label': title, [toggleHook]: true },
      el('span', { class: 'pr-toggle__knob' }),
    ),
  );

  const engrave = el(
    'div', { class: 'pr-addon' },
    addonHead(labels.engrave, labels.engravePrice, labels.engraveSub, 'data-engrave-toggle'),
    el(
      'div', { class: 'pr-addon__fields', 'data-engrave-fields': true, hidden: true },
      el(
        'div', { class: 'pr-subfield' },
        el('label', { class: 'pr-subfield__label', for: wordId, text: labels.word }),
        el('input', {
          class: 'pr-input', id: wordId, type: 'text', maxlength: String(WORD_MAX),
          placeholder: labels.wordPlaceholder || '', 'data-engrave-word': true,
        }),
      ),
      el(
        'div', { class: 'pr-subfield' },
        el('span', { class: 'pr-subfield__label', text: labels.foil }),
        el(
          'div', { class: 'pr-textswatches', role: 'radiogroup', 'aria-label': labels.foil },
          ...(config.foils || []).map((foil) => el(
            'button',
            {
              type: 'button', class: 'pr-textswatch', role: 'radio', 'aria-checked': 'false',
              'aria-label': foil.label, 'data-engrave-color': true, 'data-foil': foil.id,
            },
            el('span', { class: 'pr-textswatch__chip', style: '--swatch: ' + foil.hex }),
          )),
        ),
      ),
      el(
        'div', { class: 'pr-subfield' },
        el('span', { class: 'pr-subfield__label', text: labels.orient }),
        el(
          'div',
          { class: 'pr-segmented', role: 'radiogroup', 'aria-label': labels.orient, 'data-engrave-orient': true },
          ...(config.orientations || []).map((orient) => el(
            'button',
            { type: 'button', class: 'pr-segment', role: 'radio', 'aria-checked': 'false', 'data-orient': orient.id },
            el('span', { class: 'pr-segment__icon pr-segment__icon--' + (orient.id === 'deitado' ? 'h' : 'v'), 'aria-hidden': 'true' }),
            el('span', { text: orient.label }),
          )),
        ),
      ),
    ),
  );

  // Same markup the section emits for a swatch, check mark included — the
  // selected state is drawn by that `<svg>`, not by the chip alone.
  const colorField = (config.colors || []).length && el(
    'div', { class: 'pr-field' },
    el(
      'div', { class: 'pr-field__labelrow' },
      el('span', { class: 'pr-field__label', text: labels.color }),
      el('span', { class: 'pr-field__value', 'data-color-readout': true }),
    ),
    el(
      'div', { class: 'pr-swatches', role: 'radiogroup', 'aria-label': labels.color, 'data-color-swatches': true },
      ...config.colors.map((color, index) => el(
        'button',
        {
          type: 'button', class: 'pf-swatch', 'data-color-swatch': true, 'data-index': index,
          'data-label': color.label, role: 'radio', 'aria-checked': 'false', 'aria-label': color.label,
        },
        el('span', { class: 'pf-swatch__chip', style: '--swatch: ' + color.swatch }),
        el(
          'svg',
          {
            class: 'pf-swatch__check', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
            'stroke-width': '3.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true',
          },
          el('path', { d: 'M20 6 9 17l-5-5' }),
        ),
      )),
    ),
  );

  const form = el(
    'div', { class: 'pr-config pr-config--panel' },
    colorField,
    colorField && el('div', { class: 'pf-rule' }),
    engrave,
  );

  // The extra photo is a real add-on only when the section offers one; the
  // island reports `photo: null` otherwise, exactly as the PDP hides the block.
  if (config.photo) {
    form.append(el('div', { class: 'pf-rule' }));
    form.append(el(
      'div', { class: 'pr-addon' },
      addonHead(labels.photo, labels.photoPrice, labels.photoSub, 'data-photo-toggle'),
      el(
        'div', { class: 'pr-addon__fields', 'data-photo-fields': true, hidden: true },
        el('p', { class: 'pr-photo-note', text: labels.photoNote }),
      ),
    ));
  }
  return form;
}

/**
 * Open one picture-frame line inside the drawer.
 *
 * Throwing is meaningful here: the host answers it by falling back to the link's
 * own href, which is the `?edit=` PDP — the same editor, one navigation away.
 *
 * `doc` and `http` are injected so the wiring below — the save protocol, the
 * event round trip, the requests that actually reach the cart — can be driven by
 * a test rather than only by a browser.
 */
export async function mountPanel(cartEdit, { root, editKey, productUrl }, { doc = pageDocument, http = browserHttp, sectionsUrl } = {}) {
  const productDocument = await cartEdit.loadProductDocument(productUrl);
  const island = productDocument.getElementById('pr-config');
  if (!island) throw new Error('pdp-porta-retrato: product page carries no #pr-config');
  const config = JSON.parse(island.textContent);

  const cart = await http.getJson(config.routes.cart);
  const group = cart.ok ? readGroup(cart.data, editKey, config) : null;
  if (!group) throw new Error('pdp-porta-retrato: line ' + editKey + ' is no longer in the cart');
  if (group.gift) throw new Error('pdp-porta-retrato: a Brinde frame is not edited here');

  const form = buildPanelForm(config, doc);
  // The id the drawer's SAVE button points `aria-describedby` at, so a failed
  // save is announced with the button and not only in the scroll area above it.
  const errorEl = makeEl(doc)('p', { class: 'pr-atc-notice', id: 'cart-edit-error', role: 'alert', hidden: true });
  form.append(errorEl);
  root.append(form);

  let state = { ...group.state };
  writeState(form, state);

  let busy = false;
  let error = '';
  let destroyed = false;
  // Set once the replacement is in the cart and only the old group's removal is
  // left, so a retry finishes that step instead of adding a second copy.
  let pendingSave = null;

  const emitState = () => {
    root.dispatchEvent(new CustomEvent('fn:edit-state', { detail: { busy, canSave: !busy, error } }));
  };
  const refresh = () => { state = readState(form, state); };

  // The colour is a radio group like the others, but its readout has to follow
  // the choice — so the click writes the state back through writeState instead
  // of only marking the button.
  all(form, '[data-color-swatch]').forEach((swatch) => {
    swatch.addEventListener('click', () => {
      setSelected(all(form, '[data-color-swatch]'), (other) => other === swatch);
      refresh();
      writeState(form, state);
    });
  });

  all(form, '[data-engrave-color], [data-engrave-orient] [data-orient]').forEach((button) => {
    button.addEventListener('click', () => {
      const siblings = all(button.parentElement, button.hasAttribute('data-foil') ? '[data-engrave-color]' : '[data-orient]');
      setSelected(siblings, (other) => other === button);
      refresh();
    });
  });

  bindWordInput(form.querySelector('[data-engrave-word]'), refresh);

  [['[data-engrave-toggle]', '[data-engrave-fields]'], ['[data-photo-toggle]', '[data-photo-fields]']]
    .forEach(([toggleSelector, fieldsSelector]) => {
      const toggle = form.querySelector(toggleSelector);
      if (!toggle) return;
      toggle.addEventListener('click', () => {
        setSwitch(toggle, form.querySelector(fieldsSelector), toggle.getAttribute('aria-checked') !== 'true');
        refresh();
      });
    });

  const onSave = async () => {
    if (busy || destroyed) return;
    busy = true;
    error = '';
    errorEl.hidden = true;
    emitState();
    try {
      const sections = await saveGroup({
        config,
        state,
        editKey,
        fallbackKeys: group.keys,
        origins: group.origins || {},
        http,
        pending: pendingSave,
        sectionsUrl: sectionsUrl != null ? sectionsUrl : (pageWindow ? pageWindow.location.pathname : ''),
      });
      if (destroyed) return;
      root.dispatchEvent(new CustomEvent('fn:edit-saved', { detail: { sections } }));
    } catch (saveError) {
      if (saveError && saveError.pending) pendingSave = saveError.pending;
      if (destroyed) return;
      // The replacement went in and the old copy stayed: re-render the bag behind
      // this panel so the line the message asks them to delete is really there.
      const stranded = saveError && saveError.pending && saveError.pending.sections;
      if (stranded) doc.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: stranded } }));
      error = saveError.message || (config.strings || {}).addError || '';
      errorEl.textContent = error;
      errorEl.hidden = false;
    } finally {
      busy = false;
      if (!destroyed) emitState();
    }
  };
  root.addEventListener('fn:edit-save', onSave);

  emitState();
  return {
    destroy() {
      destroyed = true;
      root.removeEventListener('fn:edit-save', onSave);
    },
  };
}

// Claim the `porta-retrato` surface. Registration is order-tolerant: the host
// lazy-imports this module on click, but the PDP's own eager script can run
// first, and execution order between two module scripts is not something to
// rely on.
function registerPortaRetratoSurface(cartEdit) {
  cartEdit.register('porta-retrato', (context) => mountPanel(cartEdit, context));
}

if (pageWindow && pageWindow.fnCartEdit) {
  registerPortaRetratoSurface(pageWindow.fnCartEdit);
} else if (pageDocument) {
  pageDocument.addEventListener('fn:cart-edit-ready', () => registerPortaRetratoSurface(pageWindow.fnCartEdit), { once: true });
}

// ── PDP auto-boot ──────────────────────────────────────────────────────────
// A document carrying a `.pr` section IS the picture-frame PDP. On every other
// page this finds nothing and the module is only the drawer surface above.
if (pageDocument) {
  const boot = () => { pageDocument.querySelectorAll('.pr').forEach(initPortaRetrato); };
  if (pageDocument.readyState === 'loading') pageDocument.addEventListener('DOMContentLoaded', boot);
  else boot();
}
